import { FC } from 'react'
import * as cn from 'classnames'
import styles from 'components/features/coaching/advisorMeeting/AdvisorRequestTopic/AdvisorRequestTopic.styl'
import { GridSelectController } from 'components/atoms/inputs/GridSelect/GridSelect'
import { advisorRequestQuestions } from 'components/features/coaching/advisorMeeting/fixtures/requestQuestions'
import { useFormContext } from 'react-hook-form'
import { PaneNote } from 'components/features/coaching/advisorMeeting/PaneNote/PaneNote'
import { TopicStepInputFields } from 'components/features/coaching/advisorMeeting/types/floatPane'
import { FutureInputDecorationLabel } from 'components/features/future/FutureInputDecoration/FutureInputDecorationLabel'
import { InputStateTheme } from 'components/atoms/inputs/InputStateTheme/InputStateTheme'
import { ThemeProvider } from 'components/features/theme/ThemeProvider/ThemeProvider'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import { MaskedInputController } from 'components/atoms/inputs/maskedInput/MaskedInput/MaskedInput'
import { maskCurrencyFromZero, maskUSZipCode } from 'components/atoms/inputs/maskedInput'
import { FutureInput } from 'components/atoms/inputs/FutureInput/FutureInput'
import { AdvisorMeetingWizardLandingSpecificFields } from 'components/features/coaching/advisorMeeting/types/requests'
import { useFlexClassNames } from 'hooks/useClassNames'

type AdvisorRequestTopicFormProps = {
  fields: TopicStepInputFields
  onSubmit: () => void
}

export const AdvisorRequestTopicForm: FC<AdvisorRequestTopicFormProps> = (props) => {
  const { fields, onSubmit } = props
  const flexClasses = useFlexClassNames({ gap: 'medium', marginBottom: 'medium' })

  const { register, formState: { errors }} = useFormContext()
  const { flow } = fields

  return (
    <form onSubmit={ onSubmit } className={ cn(styles.advisorRequestTopic, styles[flow], flexClasses) }>
      <PaneNote>
        <div className={ styles.topicsButtons }>
          { flow === 'landing' && (
            <label>
              <h4>
                { advisorRequestQuestions.topic.question }
              </h4>
            </label>
          )}
          <GridSelectController
            theme='advisorTopic'
            selectedTheme='advisorTopicSelected'
            name='topics'
            options={ advisorRequestQuestions.topic.options }
            max={ 3 }
          />
        </div>

        { !!errors.topics && (
          <h6 data-qa-marker='error'>
            Please select at least one topic
          </h6>
        )}
      </PaneNote>

      { flow === 'landing' && (
        <LandingFields />
      )}

      <PaneNote>
        <InputStateTheme hasError={ !!errors.notes }>
          <FutureInputDecorationLabel>
            { flow !== 'landing' && (
              <>
                { advisorRequestQuestions.notes.question }
              </>
            )}
            { flow === 'landing' && (
              <>
                Notes for your coach
              </>
            )}
          </FutureInputDecorationLabel>
          <textarea className={ cn(styles.textarea, { [styles.error]: !!errors.notes }) } id='notes' { ...register('notes') } />
        </InputStateTheme>
        { !!errors.notes && (
          <h6 data-qa-marker='error'>
            Add any information that might help your coach
          </h6>
        )}
        <sub>
          The information you provide above may be shared with third-party coaches. Don't include any sensitive personal or financial information.
        </sub>
      </PaneNote>
    </form>
  )
}

export const LandingFields: FC = () => {
  const { formState: { errors }} = useFormContext<AdvisorMeetingWizardLandingSpecificFields>()
  return (
    <>
      <ThemeProvider futureFieldTitle='regular'>
        <FutureInputDecoration
          label='Your Annual Income'
          error={ errors.income?.message }
        >
          <div className={ styles.incomeInputWrapper }>
            <MaskedInputController
              className={ cn(styles.incomeInput, { [styles.error]: !!errors.income }) }
              options={ maskCurrencyFromZero }
              name='income'
              inputMode='numeric'
              renderer={ FutureInput }
              autoComplete='off'
            />
            <div className={ styles.incomeInputUnits }>
              per year
            </div>
          </div>
        </FutureInputDecoration>

        <FutureInputDecoration
          label='Your Zip Code'
          error={ errors.zip5?.message }
        >
          <MaskedInputController
            className={ cn(styles.zipInput, { [styles.error]: !!errors.zip5 }) }
            options={ maskUSZipCode }
            name='zip5'
            inputMode='numeric'
            renderer={ FutureInput }
            autoComplete='off'
          />
        </FutureInputDecoration>
      </ThemeProvider>
    </>
  )
}
